import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_boarding-school-fit/artifacts/5kmsljm9_ESA%20Final%20Logo.png";
const CAL_LINK = "https://cal.com/elite-school-advisory/30min";

const navItems = [
  { label: "Approach", href: "#method" },
  { label: "Services", href: "#services" },
  { label: "Schools", href: "#schools" },
  { label: "Results", href: "#proof" },
  { label: "FAQs", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  const openCalendar = () => {
    window.open(CAL_LINK, "_blank");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-[#FDFCF8]/95 backdrop-blur-md shadow-sm border-b border-[#E2E8F0]"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <img
              src={LOGO_URL}
              alt="Elite School Advisory"
              className="h-16 sm:h-20 md:h-[110px] w-auto md:-my-6"
              data-testid="header-logo"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className="nav-link text-[#1A202C] text-sm font-medium tracking-wide hover:text-[#2B4C7E] transition-colors"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button
              onClick={openCalendar}
              className="bg-[#2B4C7E] text-white hover:bg-[#1E3A60] rounded-[2px] px-6 py-5 text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
              data-testid="header-cta-btn"
            >
              Book a Strategy Call
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-[#2B4C7E]"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#FDFCF8] border-b border-[#E2E8F0]"
          >
            <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className="text-left text-[#1A202C] text-base font-medium py-2 hover:text-[#2B4C7E] transition-colors"
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </button>
              ))}
              <Button
                onClick={openCalendar}
                className="bg-[#2B4C7E] text-white hover:bg-[#1E3A60] rounded-[2px] px-6 py-5 text-sm font-medium mt-4"
                data-testid="mobile-cta-btn"
              >
                Book a Strategy Call
              </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
